import axios from 'axios';

// Reddit数据服务 - 生产版本
import { RedditPost, SubredditData, APIResponse } from '../types/reddit';

// API配置 - 自动检测环境
const getApiBaseUrl = () => {
  // 开发环境
  if (import.meta.env.DEV || window.location.hostname === 'localhost') {
    return 'http://localhost:3001';
  }
  
  // 生产环境 - 使用Cloudflare Worker
  return 'https://reddit-hotboard-api.518004.workers.dev';
};

const API_BASE_URL = getApiBaseUrl();

class RedditService {
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  private healthStatus = { isHealthy: true, lastCheck: 0 };

  // 健康检查
  async checkHealth(): Promise<boolean> {
    const now = Date.now();
    
    // 5分钟内已检查过
    if (now - this.healthStatus.lastCheck < 300000 && this.healthStatus.isHealthy) {
      return this.healthStatus.isHealthy;
    }

    try {
      console.log('🔍 检查API健康状态...');
      const response = await fetch(`${API_BASE_URL}/api/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(5000) // 5秒超时
      });

      const isHealthy = response.ok;
      this.healthStatus = { isHealthy, lastCheck: now };
      
      if (isHealthy) {
        console.log('✅ API服务健康');
      } else {
        console.log('❌ API服务异常');
      }
      
      return isHealthy;
    } catch (error) {
      console.error('健康检查失败:', error);
      this.healthStatus = { isHealthy: false, lastCheck: now };
      return false;
    }
  }

  // 从缓存获取数据
  private getFromCache(key: string): any | null {
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    const now = Date.now();
    if (now - cached.timestamp > cached.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    console.log(`🔍 前端缓存命中: ${key}`);
    return cached.data;
  }

  // 设置缓存
  private setCache(key: string, data: any, ttlMs: number = 300000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttlMs
    });
    console.log(`💾 前端缓存更新: ${key}`);
  }

  // 获取所有subreddits数据
  async getAllSubreddits(): Promise<SubredditData[]> {
    const cacheKey = 'all_subreddits';
    
    // 尝试从前端缓存获取
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      console.log('🔄 获取所有Subreddit数据...');
      
      const response = await fetch(`${API_BASE_URL}/api/reddit/subreddits`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(30000) // 30秒超时
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: SubredditData[] = await response.json();
      
      // 验证数据格式
      if (Array.isArray(data) && data.length > 0) {
        this.setCache(cacheKey, data, 600000); // 10分钟缓存
        console.log(`✅ 成功获取 ${data.length} 个subreddit数据`);
        return data;
      } else {
        throw new Error('API返回数据格式异常');
      }

    } catch (error) {
      console.error('获取Subreddit数据失败:', error);
      
      // 降级到本地模拟数据
      console.log('🔄 使用降级数据...');
      const fallbackData = this.generateFallbackData();
      this.setCache(cacheKey, fallbackData, 60000); // 1分钟缓存降级数据
      return fallbackData;
    }
  }

  // 获取单个subreddit数据
  async getSubredditData(name: string): Promise<SubredditData> {
    const cacheKey = `subreddit_${name}`;
    
    // 尝试从前端缓存获取
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      console.log(`🔄 获取 r/${name} 数据...`);
      
      const response = await fetch(`${API_BASE_URL}/api/reddit/subreddit/${name}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(15000) // 15秒超时
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: SubredditData = await response.json();
      
      // 验证数据格式
      if (data && data.name && Array.isArray(data.posts)) {
        this.setCache(cacheKey, data, 600000); // 10分钟缓存
        console.log(`✅ 成功获取 r/${name} 数据`);
        return data;
      } else {
        throw new Error('API返回数据格式异常');
      }

    } catch (error) {
      console.error(`获取 r/${name} 数据失败:`, error);
      
      // 降级到模拟数据
      console.log(`🔄 使用 r/${name} 降级数据...`);
      const fallbackData = this.generateSubredditFallback(name);
      this.setCache(cacheKey, fallbackData, 60000); // 1分钟缓存降级数据
      return fallbackData;
    }
  }

  // 生成降级数据
  private generateFallbackData(): SubredditData[] {
    const subreddits = ['IAmA', 'skincareaddiction', 'fragrance', 'health', 'fitness', 'technology', 'gaming', 'deals', 'bargainbin'];
    
    return subreddits.map(name => this.generateSubredditFallback(name));
  }

  // 生成单个subreddit的降级数据
  private generateSubredditFallback(name: string): SubredditData {
    const topicTemplates: { [key: string]: string[] } = {
      technology: [
        '最新AI技术突破引发热议',
        '科技巨头发布新产品',
        '开源项目获得社区支持',
        '编程语言趋势分析',
        '科技行业最新动态'
      ],
      gaming: [
        '热门游戏更新内容发布',
        '游戏社区讨论新机制',
        '电竞赛事精彩回顾',
        '独立游戏开发者分享',
        '游戏硬件性能测试'
      ],
      health: [
        '健康生活方式建议',
        '营养科学最新研究',
        '运动健身经验分享',
        '心理健康话题讨论',
        '医疗保健信息更新'
      ],
      fitness: [
        '健身训练计划分享',
        '营养补充剂效果评测',
        '运动损伤预防方法',
        '健身器材使用心得',
        '减脂增肌经验交流'
      ]
    };

    const templates = topicTemplates[name] || [
      `${name}社区热门话题`,
      `${name}用户经验分享`,
      `${name}最新动态更新`,
      `${name}专业知识讨论`,
      `${name}社区互动交流`
    ];

    const posts: RedditPost[] = templates.map((title, index) => ({
      id: `fallback_${name}_${index}_${Date.now()}`,
      title,
      author: `user_${Math.floor(Math.random() * 1000)}`,
      score: Math.floor(Math.random() * 500) + 100,
      num_comments: Math.floor(Math.random() * 150) + 25,
      selftext: `这是关于${title}的详细讨论内容，包含了社区成员的深入分析和宝贵见解...`,
      url: `https://reddit.com/r/${name}/`,
      created_utc: Date.now() / 1000 - Math.random() * 86400,
      upvote_ratio: 0.75 + Math.random() * 0.2
    }));

    return {
      name,
      displayName: `r/${name}`,
      posts,
      keywords: this.generateKeywords(name),
      trendData: this.generateTrendData(),
      stats: this.calculateStats(posts),
      lastUpdated: new Date().toISOString(),
      source: 'fallback'
    };
  }

  // 生成关键词
  private generateKeywords(subredditName: string): Array<{ word: string; count: number }> {
    const keywordSets: { [key: string]: string[] } = {
      technology: ['AI', '科技', '创新', '开发', '数据'],
      gaming: ['游戏', '玩家', '体验', '更新', '社区'],
      health: ['健康', '营养', '锻炼', '生活', '研究'],
      fitness: ['健身', '训练', '肌肉', '运动', '计划']
    };

    const keywords = keywordSets[subredditName] || ['讨论', '分享', '经验', '社区', '话题'];
    
    return keywords.map(word => ({
      word,
      count: Math.floor(Math.random() * 50) + 10
    }));
  }

  // 生成趋势数据
  private generateTrendData(): Array<{ time: string; interactions: number; posts: number; comments: number }> {
    const data = [];
    const hoursBack = 24;
    
    for (let i = hoursBack - 1; i >= 0; i--) {
      const time = new Date(Date.now() - i * 60 * 60 * 1000);
      data.push({
        time: time.toISOString(),
        interactions: Math.floor(Math.random() * 150) + 50,
        posts: Math.floor(Math.random() * 25) + 5,
        comments: Math.floor(Math.random() * 300) + 100
      });
    }
    
    return data;
  }

  // 计算统计数据
  private calculateStats(posts: RedditPost[]): { totalScore: number; avgComments: number; avgRatio: number; hotness: number } {
    if (posts.length === 0) {
      return { totalScore: 0, avgComments: 0, avgRatio: 0, hotness: 0 };
    }

    const totalScore = posts.reduce((sum, post) => sum + post.score, 0);
    const totalComments = posts.reduce((sum, post) => sum + post.num_comments, 0);
    const totalRatio = posts.reduce((sum, post) => sum + post.upvote_ratio, 0);

    return {
      totalScore,
      avgComments: Math.round(totalComments / posts.length),
      avgRatio: Number((totalRatio / posts.length).toFixed(2)),
      hotness: Math.round(totalScore / posts.length)
    };
  }

  // 清除缓存
  clearCache(): void {
    this.cache.clear();
    console.log('🧹 前端缓存已清除');
  }

  // 获取缓存统计
  getCacheStats(): { keys: number; size: string } {
    const size = JSON.stringify([...this.cache]).length;
    return {
      keys: this.cache.size,
      size: `${Math.round(size / 1024)}KB`
    };
  }

  // 获取API状态
  getApiStatus(): { baseUrl: string; isHealthy: boolean; lastCheck: string } {
    return {
      baseUrl: API_BASE_URL,
      isHealthy: this.healthStatus.isHealthy,
      lastCheck: new Date(this.healthStatus.lastCheck).toISOString()
    };
  }
}

const redditService = new RedditService();

export const getSubreddits = async (): Promise<string[]> => {
  const subreddits = await redditService.getAllSubreddits();
  return subreddits.map(sub => sub.name);
};

export const getSubredditData = async (name: string): Promise<SubredditData> => {
  return await redditService.getSubredditData(name);
};

export default redditService;